import { takeEvery, all, put, call } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import api from '../helpers/Api';
import BackendPaths from '../helpers/BackendPaths';
import * as HelperSaga from './Helper';

import { FETCH_SURVEYS_REQUEST } from '../constants/Survey';
import * as CurrentUser from '../constants/CurrentUser';
import { getLicenseFromSessionInfo } from '../helpers/User';
import * as ReportApp from '../typescript/types';
import { ReportAppActions } from '../typescript/actions/actions';

function* fetchCurrentUser() {
    let sessionInfo: ReportApp.OmniWebService.SessionInfo | null;
    let user: ReportApp.OmniWebService.User | undefined;
    try {
        sessionInfo = yield call(api, BackendPaths.sessionInfo.getPath());
        user = sessionInfo?.user;
    } catch (error) {
        // status code 401 could be returned for users that have professional
        // plan provided through institutional login
        if (error.status !== 401) {
            return yield HelperSaga.handleError(
                CurrentUser.FETCH_ME_FAILURE,
                error,
            );
        }
        sessionInfo = null;
    }

    if (!sessionInfo || !user) {
        // In case when user is not signed in at all, neither through his account, nor through
        // institutional account, user will be null. In this case we should redirect
        window.location.href = BackendPaths.loginUrl.getPath();
        return;
    }

    const licenseInfo = getLicenseFromSessionInfo(sessionInfo);

    yield put<ReportAppActions>({
        type: CurrentUser.FETCH_ME_SUCCESS,
        payload: user,
    });
    yield put<ReportAppActions>({
        type: CurrentUser.SET_SESSION_INFO,
        payload: licenseInfo,
    });
    Sentry.configureScope((scope) => {
        scope.setUser({
            id: `${user!.id}`,
            email: user!.email,
            licenceTitle: licenseInfo.licenceTitle,
        });
    });
    // surveys are used on multiple pages (/surveys - to display all surveys,
    // /report/:reportId - to display survey title if report is not saved).
    // for this reason we will load surveys in all cases. We could later think
    // about potential optimizations
    yield put<ReportAppActions>({ type: FETCH_SURVEYS_REQUEST });
}

function* signOut() {
    try {
        yield call(api, BackendPaths.railsSessionUrl.getPath(), 'DELETE');
        Sentry.configureScope((scope) => scope.setUser(null));
        window.location.href = BackendPaths.loginUrl.getPath();
    } catch (error) {
        return yield HelperSaga.handleError(
            CurrentUser.SIGN_OUT_FAILURE,
            error,
        );
    }
}

function* watchFetchCurrentUser() {
    yield takeEvery(CurrentUser.FETCH_ME_REQUEST, fetchCurrentUser);
    yield takeEvery(CurrentUser.SIGN_OUT_REQUEST, signOut);
}

export default function* () {
    yield all([watchFetchCurrentUser()]);
}
